import { useEffect, useState } from "react";
// Import the skeleton CSS once, typically at a root or global level

import qs from "qs";
import { TailSpin } from "react-loader-spinner";
import { Bar, BarChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "../api";
import { useFilters } from "./FilterContext";

interface RawData {
  month: string;
  name: string;
  eventCount: string;
}

interface ChartData {
  month: string;
  [key: string]: string | number;
}

const transformData = (rawData: RawData[]): ChartData[] => {
  const map: { [key: string]: ChartData } = {};

  rawData.forEach((item) => {
    const { month, name, eventCount } = item;
    if (!map[month]) {
      map[month] = { month };
    }
    map[month][name] = Number(eventCount);
  });

  return Object.values(map);
};

const StackableBarChart = () => {
  const [data, setData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);

  const { filters } = useFilters();

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const { data: response } = await api.get("/dashboard/by-month", {
          params: {
            routeIds: filters.routeIds,
            eventTypes: filters.eventTypes,
            ...(filters.startDate && { startDate: filters.startDate }),
            ...(filters.endDate && { endDate: filters.endDate })
          },
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "comma" }),
        });

        const transformedData = transformData(response);
        setData(transformedData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [filters]);

  // Distinct event types (excluding the 'month' key)
  const eventTypes = data.reduce((acc: string[], item) => {
    Object.keys(item).forEach((key) => {
      if (key !== "month" && !acc.includes(key)) {
        acc.push(key);
      }
    });
    return acc;
  }, []);

  const colors = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff7300",
    "#0088FE",
    "#00C49F",
  ];

  // Optional custom tooltip
  // ...

  return (
    <div className="bg-white rounded-lg shadow-md p-6 w-full h-[450px]">
      <h3 className="text-left text-2xl font-bold text-gray-800 mb-4">
        Events Over Time
      </h3>


      {loading ? (
        <div className="flex items-center justify-center w-full h-[300px]">
          <TailSpin
            height={80}
            width={80}
            color="#2563eb"
            strokeWidth={2}
            ariaLabel="oval-loading"
            visible={true}
          />
        </div>
      ) : (
        // --- Actual Chart ---
        <div className="w-full h-[370px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              barCategoryGap={8}
              barGap={2}
            >
              <XAxis dataKey="month" stroke="#666" />
              <YAxis stroke="#666" />
              <Tooltip />
              <Legend />

              {eventTypes.map((eventType, index) => (
                <Bar
                  key={eventType}
                  dataKey={eventType}
                  stackId="a"
                  fill={colors[index % colors.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default StackableBarChart;
